import type {
  ChatGeneratedPageItem,
  GeneratePageToolResult,
} from "schemas/generated/chatMessage";
import type { FileWithLatestFileVersion } from "schemas/generated/file";
import type { GeneratePageToolMetadata } from "schemas/generated/generatePageTool";
import type { generatePageTool } from "trigger/src/tools/generate-page-tool";

import React, { useState } from "react";

import { useChatId } from "@editor/components/editor/ai/chat/hooks/useChatId";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { chatRouter, chatUtils } from "@editor/trpc/chat";
import { fileVersionUtils } from "@editor/trpc/fileVersion";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@editor/trpc/nextJSProject";
import { isDevelopment } from "@editor/utils/env";

import { useUpdatePageFilesInWebContainer } from "@/features/code-execution/web-container/useUpdatePageFilesInWebContainer";
import shouldShowV2Debuggers from "@/features/react-editor/utils/shouldShowV2Debuggers";
import Button from "@replo/design-system/components/button/Button";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { skipToken } from "@tanstack/react-query";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { ArrowUpRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import LoadingVersionCard from "../../history/LoadingVersionCard";
import { VersionCard } from "../../history/VersionHistory";
import { useViewingVersion } from "../hooks/useViewingVersion";
import { useChatStoreActions } from "../store/ChatStoreContext";
import { formatElapsedTime, useElapsedTime } from "../utils/elapsedTime";
import CanceledCard from "./CanceledCard";
import ErrorCard from "./ErrorCard";
import ProgressCard from "./ProgressCard";

function PageGenerationDebug({
  children,
  toolResult,
}: {
  children: React.ReactNode;
  toolResult: GeneratePageToolResult;
}) {
  const runId = toolResult.output.value.jobId;
  const triggerLink = `https://cloud.trigger.dev/orgs/replo-7289/projects/test-quaR/env/${isDevelopment ? "dev" : "prod"}/runs/${runId}`;

  const chatId = useChatId();
  const heliconeLink = `https://us.helicone.ai/sessions/chat/${chatId}`;

  return (
    <>
      {children}
      {shouldShowV2Debuggers() && runId && (
        <div className="flex gap-2 mt-2">
          <Button
            variant="primary"
            onClick={() => {
              window.open(triggerLink, "_blank");
            }}
            endEnhancer={<ArrowUpRight className="w-4 h-4" />}
          >
            Trigger Job
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              window.open(heliconeLink, "_blank");
            }}
            endEnhancer={<ArrowUpRight className="w-4 h-4" />}
          >
            Helicone Session
          </Button>
        </div>
      )}
    </>
  );
}

function usePageGenerationRun(
  toolResult: GeneratePageToolResult,
  toolMessageId?: string,
) {
  const projectId = useCurrentProjectId();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const [jobCompleted, setJobCompleted] = useState(false);
  const { addActiveTriggerJob, removeActiveTriggerJob } = useChatStoreActions();
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const jobId = toolResult.output.value.jobId;
  const publicAccessToken = toolResult.output.value.publicAccessToken;
  const updatePageFilesInWebContainer = useUpdatePageFilesInWebContainer();

  React.useEffect(() => {
    if (chatId && jobId && !jobCompleted) {
      addActiveTriggerJob(chatId, {
        jobId,
        jobType: "generate-page-tool",
      });
    }
  }, [jobId, chatId, jobCompleted, addActiveTriggerJob]);

  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      void chatUtils.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const { mutate: addMessage } = chatRouter.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  async function handleGeneratePageSuccess(
    createdFile: FileWithLatestFileVersion,
  ) {
    void nextJSProjectUtils.listPages.invalidate({ projectId });
    void fileVersionUtils.findByFileId.invalidate({
      projectId,
      fileId: createdFile.id,
    });

    if (!isServerSandboxEnabled) {
      await updatePageFilesInWebContainer({
        pages: [{ id: createdFile.id }],
      });
    }

    // Automatically switch to the created page in preview panel
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("pageId", createdFile.id);
    setSearchParams(newSearchParams);

    // Update the message to mark as completed with created page ID
    if (projectId && chatId && toolMessageId) {
      updateMessage({
        id: toolMessageId,
        projectId,
        content: [
          {
            type: "tool-result",
            toolCallId: toolResult.toolCallId,
            toolName: toolResult.toolName,
            output: {
              type: "json",
              value: {
                ...toolResult.output.value,
                createdPageId: createdFile.id,
                fileVersionId: createdFile.latestFileVersion?.id,
                isCompleted: true,
              },
            },
          },
        ],
      });

      // Add a user message about the created page
      const generatedPageItem: ChatGeneratedPageItem = {
        type: "generatedPage",
        pageName: createdFile.name ?? "Generated Page",
        pageId: createdFile.id,
      };

      addMessage({
        projectId,
        chatId,
        content: [
          {
            type: "text",
            contentType: "generatedPage" as const,
            text: JSON.stringify(generatedPageItem),
          },
        ],
        role: "user",
      });

      // Add an assistant message about the page generation
      addMessage({
        projectId,
        chatId,
        content: [
          {
            type: "text",
            contentType: "text" as const,
            text: "Your page has been created successfully! Let me know if you want to make any changes to it.",
          },
        ],
        role: "assistant",
      });
    }
  }

  const { run, error } = useRealtimeRun<typeof generatePageTool>(jobId, {
    accessToken: publicAccessToken,
    enabled: Boolean(jobId && publicAccessToken && !jobCompleted),
    onComplete: (completedRun) => {
      setJobCompleted(true);

      if (chatId) {
        removeActiveTriggerJob(chatId, completedRun.id);
      }

      if (
        completedRun?.status === "COMPLETED" &&
        completedRun?.output?.createdPage &&
        projectId
      ) {
        void handleGeneratePageSuccess(completedRun.output.createdPage);
      }
    },
  });

  return { run, error };
}

function PageGenerationProgressCard({
  toolResult,
  toolMessageId,
}: {
  toolResult: GeneratePageToolResult;
  toolMessageId?: string;
}) {
  const { run, error } = usePageGenerationRun(toolResult, toolMessageId);
  const metadata = run?.metadata as GeneratePageToolMetadata | undefined;
  const elapsedTime = useElapsedTime(run?.startedAt ?? null);

  const isFailedRun =
    run &&
    (run.output?.success === false || Boolean(error) || Boolean(run.error));
  const isCanceledRun = run && run.status === "CANCELED";

  if (isCanceledRun) {
    return <CanceledCard />;
  }

  if (isFailedRun) {
    return (
      <ErrorCard
        title="We couldn't generate your page"
        subtitle="Something went wrong while generating your page. Please try again or reach out to us at support@replo.com"
      />
    );
  }

  const metadataProgress = metadata?.tasks?.map((t, i) => ({
    id: (i + 1).toString(),
    headline: t.task,
    isCompleted: t.isComplete,
  }));

  const progress = metadataProgress
    ? [
        {
          id: "0",
          headline: "Created tasks",
          isCompleted: true,
        },
        ...metadataProgress,
      ]
    : [
        {
          id: "0",
          headline: "Creating tasks...",
          isCompleted: false,
        },
      ];

  return (
    <ProgressCard
      startEnhancer={
        <img
          src="/images/page-generation.png"
          alt="Page Generation Loading"
          className="w-10 h-10"
        />
      }
      endEnhancer={
        run?.startedAt ? (
          formatElapsedTime(elapsedTime)
        ) : (
          <Spinner variant="ai" size={16} />
        )
      }
      title={metadata?.pageName ? `${metadata.pageName}` : "Generating Page"}
      progress={progress}
      subtitle="Building will take a few minutes"
      showCondensedProgress={false}
    />
  );
}

function PageGenerationCompletedCard({
  createdPageId,
  fileVersionId,
}: {
  createdPageId: string;
  fileVersionId?: string;
}) {
  const projectId = useCurrentProjectId();
  const {
    fileVersion,
    isFileVersionLoading,
    isCurrentVersion,
    isViewingVersion,
  } = useViewingVersion(fileVersionId);

  const { data: page } = nextJSProjectRouter.getPage.useQuery(
    projectId && createdPageId
      ? {
          projectId,
          id: createdPageId,
        }
      : skipToken,
  );

  if (!fileVersion) {
    if (!page || isFileVersionLoading) {
      return <LoadingVersionCard />;
    }

    return (
      <ErrorCard
        title="We couldn't generate your page"
        subtitle="Something went wrong while generating your page. Please try again or reach out to us at support@replo.com"
      />
    );
  }

  return (
    <VersionCard
      version={fileVersion}
      isViewingVersion={isViewingVersion}
      isCurrentVersion={isCurrentVersion}
    />
  );
}

export default function PageGenerationBlock({
  toolResult,
  toolMessageId,
}: {
  toolResult: GeneratePageToolResult | null;
  toolMessageId?: string;
}) {
  if (!toolResult) {
    return (
      <ProgressCard
        startEnhancer={
          <img
            src="/images/page-generation.png"
            alt="Page Generation Loading"
            className="w-10 h-10"
          />
        }
        endEnhancer={<Spinner variant="ai" size={16} />}
        title="Generating Page"
        subtitle="Building will take a few minutes"
        showCondensedProgress={false}
      />
    );
  }

  const isFailed =
    !toolResult.output.value.success || toolResult.output.value.error;

  if (isFailed) {
    return (
      <PageGenerationDebug toolResult={toolResult}>
        <ErrorCard
          title="We couldn't generate your page"
          subtitle="Something went wrong while generating your page. Please try again or reach out to us at support@replo.com"
        />
      </PageGenerationDebug>
    );
  }

  const createdPageId = toolResult.output.value.createdPageId;

  if (createdPageId) {
    return (
      <PageGenerationDebug toolResult={toolResult}>
        <PageGenerationCompletedCard
          createdPageId={createdPageId}
          fileVersionId={toolResult.output.value.fileVersionId}
        />
      </PageGenerationDebug>
    );
  }

  return (
    <PageGenerationDebug toolResult={toolResult}>
      <PageGenerationProgressCard
        toolResult={toolResult}
        toolMessageId={toolMessageId}
      />
    </PageGenerationDebug>
  );
}
